import * as ko from 'knockout';
import { Observable, ObservableArray, Computed } from 'knockout';
import GroupsRepository from '@/Repositories/groupsRepository';
import menuDataRepository from '@/Repositories/menuDataRepository';
import logger from '@/Utils/logger';
import redirectHelper from '@/Utils/redirectHelper';
import ValidationRulesService from '@/Validation/validationRulesService';
import contextData from '@/contextData';
import constants from '@/constants';
import DualListBoxItem from '@/Components/dualListBox/types/dualListBoxItem';
import DualListBoxIconDetails from '@/Components/dualListBox/types/dualListBoxIconDetails';
import GroupDetailsValidationModel from '@/Models/groupManagement/groupDetailsValidationModel';
import { GroupDto } from '@/Models/groupManagement/groupDto';
import { PersonaDisplayNameSummaryDto } from '@/Models/groupManagement/personaDisplayNameSummaryDto';
import { GroupMemberDto } from '@/Models/groupManagement/groupMemberDto';
import PortalSettingsProvider from '@/Utils/portalSettingsProvider';
import { AccountType } from '@/Types/Enums/accountType';
import { defineReactiveProps } from '@/VueCore/utils/vueAppBuilder';
import { VueReactivePropsBuilder } from '@/VueCore/utils/vueAppBuilder';

import template from '@/PageComponents/Portal/groupDetails/groupDetails.html';

class GroupsDetailsViewModel {
  public titleResourceKey: Observable<string>;
  public formSubmitted: Observable<boolean>;
  public serverErrors: ObservableArray;
  public clientError: Observable;
  public formDeleted: Observable<boolean>;
  // Page mode related values and observables
  public pageMode: string;
  public groupDetails: any;
  public inAddMode: Observable<boolean>;
  public inEditMode: Observable<boolean>;
  public showDeletePrompt: Observable<boolean>;
  public selectedItemIdObservable: Observable<string>;
  public updateGroupFunc: (groupDto: any) => any;
  public deleteGroupFunc: (id: string) => any;
  public isDataAvailable: Observable<boolean>;
  public groupSaveWarningModalProps: any;
  public preRegisteredUsersAssignedCount: Computed<number>;
  private businessId: string = contextData.userData.business.businessId;
  private validationRulesService: any = null;
  private showGroupSaveWarningModal: Observable<boolean>;
  private readonly availableUsers: ObservableArray<DualListBoxItem>;
  private readonly assignedUsers: ObservableArray<DualListBoxItem>;
  private readonly validationRules: any = null;
  private readonly tabItems: Array<any>;
  private readonly selectedTabItem: Observable;
  private readonly selectedTabComponentName: Observable<string>;
  private readonly initialCoordinate: Observable<Array<number>>;
  private readonly defaultCoordinate: Array<number>;
  private readonly showLocation: boolean;
  private readonly hasFocus: Observable<Array<boolean>>;

  // params passed to tab components
  private tabParams: any;
  private usersTabProps: any;

  constructor(params: any) {

    this.defaultCoordinate = contextData.userData.business.coordinate ?
      contextData.userData.business.coordinate :
      contextData.portalSettings.defaultMapView.coordinate;

    this.initialCoordinate = ko.observable(this.defaultCoordinate);
    this.formSubmitted = ko.observable(false);
    this.serverErrors = ko.observableArray();
    this.clientError = ko.observable(null);
    this.formDeleted = ko.observable(false);
    this.pageMode = params.pageMode;
    this.titleResourceKey = ko.observable('');
    this.inAddMode = ko.observable(false);
    this.inEditMode = ko.observable(false);
    this.showDeletePrompt = ko.observable(false);
    this.groupDetails = new GroupDetailsValidationModel();
    this.isDataAvailable = ko.observable(false);
    this.availableUsers = ko.observableArray();
    this.assignedUsers = ko.observableArray();
    this.selectedTabComponentName = ko.observable('group-users-tab');
    this.selectedItemIdObservable = params.selectedItemId ?? ko.observable(null);
    this.updateGroupFunc = params.updateGroupFunc;
    this.deleteGroupFunc = params.deleteGroupFunc;
    this.groupDetails.coordinate(this.defaultCoordinate);
    this.validationRulesService = new ValidationRulesService(this.groupDetails);
    this.validationRules = params.validationRules;
    this.validationRulesService.applyValidation(this.validationRules);
    this.selectedTabItem = ko.observable();
    this.showLocation = PortalSettingsProvider.getAccountType() === AccountType.Business;
    this.tabItems = menuDataRepository.getGroupDetailsTabs();
    this.showGroupSaveWarningModal = ko.observable(false);
    this.hasFocus = ko.observable(true);

    this.preRegisteredUsersAssignedCount = ko.computed(():number => {
      return this.assignedUsers().filter(user => user.isChecked() && user.isPersonaPreregistered()).length;
    });
    this.groupSaveWarningModalProps = defineReactiveProps({
      preRegisteredUsersAssignedCount: this.preRegisteredUsersAssignedCount,
      onClose: () => {
        this.showGroupSaveWarningModal(false);
      },
      onSave: () => {
        this.showGroupSaveWarningModal(false);
        this.inAddMode() ? this.addGroup() : this.updateGroup();
      }
    });
    this.selectedTabItem.subscribe(newItem => {
      this.selectedTabComponentName(newItem.data.pageComponentName);
    });

    this.setActiveTabItem();

    if (this.pageMode === constants.pageMode.edit) {
      this.setEditPageModeObservables();
      this.selectItemIdChangeHandler(this.selectedItemIdObservable());

    } else if (this.pageMode === constants.pageMode.add) {
      this.setAddPageModeObservables();
      this.setAvailableUsers(params.allPersonas);
      this.isDataAvailable(true);

    } else {
      logger.error('UnhandledError', `The page mode ${this.pageMode} is not valid.`);
    }

    // Set vue props for DualListBox.vue to pass them to usersTab
    this.usersTabProps = new VueReactivePropsBuilder()
      .withVModel('availableItems', this.availableUsers())
      .withVModel('assignedItems', this.assignedUsers())
      .withVModel('selectedItemId', this.selectedItemIdObservable())
      .on('availableItemsUpdated', newValue => {
        const uncheckedAvailableItems = newValue.map((x: DualListBoxItem) => {
          x.isChecked(false);
          return x;
        });
        this.availableUsers(uncheckedAvailableItems);
      })
      .on('assignedItemsUpdated', newValue => {
        this.assignedUsers(newValue);
      })
      .on('isCheckedUpdated', (itemId, newValue) => {
        this.assignedUsers().find((user: DualListBoxItem) => user.id === itemId).isChecked(newValue);
      })
      .build();

    this.availableUsers.subscribe((newValue : Array<DualListBoxItem>) => {
      this.usersTabProps.availableItems = [...newValue];
    });

    this.assignedUsers.subscribe((newValue : Array<DualListBoxItem>) => {
      this.usersTabProps.assignedItems = [...newValue];
    });

    this.tabParams = {
      pageMode: this.pageMode,
      isDataAvailable: this.isDataAvailable,
      availableUsers: this.availableUsers,
      assignedUsers: this.assignedUsers,
      initialCoordinate: this.initialCoordinate,
      groupDetails: this.groupDetails,
      selectedItemIdObservable: this.selectedItemIdObservable,
      selectedTabItemObservable: this.selectedTabItem,
      usersTabProps: this.usersTabProps
    };
  }

  public setActiveTabItem = (): void => {

    let currentItem;
    let i;
    let activeItem;
    for (i = 0; i < this.tabItems.length; i++) {
      currentItem = this.tabItems[i];
      currentItem.isActive(false);
      if (currentItem.data.pageComponentName === this.selectedTabComponentName()) {
        activeItem = currentItem;
      }
    }
    activeItem.isActive(true);
    this.selectedTabItem(activeItem);
  };

  public initiateAddGroup() {
    if (this.preRegisteredUsersAssignedCount() > 0) {
      this.showGroupSaveWarningModal(true);
      return;
    }
    this.addGroup();
  }

  public addGroup = (): any => {
    this.clientError(null);
    this.serverErrors([]);
    if (!this.isSavingAllowed()) {
      return;
    }
    if (!this.groupDetails.isLocationAssigned()) {
      this.resetLocation();
    }
    GroupsRepository.addNewGroup(this.businessId, this.groupDetails, this.assignedUsers())
      .then(() => {
        logger.success('GroupAddedSummary');
        redirectHelper.redirectToHash('#groups');
      })
      .catch((jqXhr: any) => {
        this.formSubmitted(false);

        if (jqXhr.serverErrorMessages) {
          this.serverErrors(jqXhr.serverErrorMessages);
          return;
        }

        if (!jqXhr.errorHasBeenLogged) {
          logger.error('UnexpectedErrorWhileAddingGroup', null, jqXhr);
        }
      });
  };

  public initiateUpdateGroup() {
    if (this.preRegisteredUsersAssignedCount() > 0) {
      this.showGroupSaveWarningModal(true);
      return;
    }
    this.updateGroup();
  }

  public updateGroup = (): any => {
    this.clientError(null);
    this.serverErrors([]);
    if (this.selectedItemIdObservable() !==null && !this.isSavingAllowed()) {
      return;
    }

    if (!this.groupDetails.isLocationAssigned()) {
      this.resetLocation();
    }

    GroupsRepository.updateGroup(this.selectedItemIdObservable(), this.businessId, this.groupDetails, this.assignedUsers())
      .then((groupDto: GroupDto) => {
        logger.success('GroupUpdatedSummary');
        this.formSubmitted(false);
        this.updateGroupFunc(groupDto);
      })
      .catch((jqXhr: any) => {
        this.formSubmitted(false);

        if (jqXhr.serverErrorMessages) {
          this.serverErrors(jqXhr.serverErrorMessages);
          return;
        }

        if (!jqXhr.errorHasBeenLogged) {
          logger.error('UnexpectedErrorWhileUpdatingGroup', null, jqXhr);
        }
      });
  };

  public deleteGroup = (): void => {
    this.showDeletePrompt(true);
  };

  deleteGroupConfirmed = (): void => {
    this.showDeletePrompt(false);
    this.formDeleted(true);
    this.serverErrors([]);

    const itemIdToDelete = this.selectedItemIdObservable();
    GroupsRepository.deleteGroup(this.businessId, itemIdToDelete)
      .then(() => {
        this.deleteGroupFunc(itemIdToDelete);
        logger.success('GroupDeletedSummary');
      })
      .catch((jqXhr: any) =>{
        this.formDeleted(false);

        if (jqXhr.serverErrorMessages) {
          this.serverErrors(jqXhr.serverErrorMessages);
          return;
        }

        if (!jqXhr.errorHasBeenLogged) {
          logger.error('UnexpectedErrorWhileDeletingGroup', null, jqXhr);
        }
      });
  };

  private isSavingAllowed = (): boolean => {
    if (this.groupDetails.clientErrors().length > 0) {
      this.groupDetails.clientErrors.showAllMessages(true);
      return false;
    }
    const validationFailureInfo = this.getStatusValidationInfo();
    if (validationFailureInfo) {
      this.clientError(validationFailureInfo.resourceKey);
      this.selectedTabComponentName(validationFailureInfo.tabName);
      this.setActiveTabItem();
      return false;
    }
    this.formSubmitted(true);
    return true;
  };

  private selectItemIdChangeHandler = (newValue: string): void => {
    if (this.pageMode !== constants.pageMode.edit) {
      return;
    }
    this.isDataAvailable(false);
    if (newValue !== null) {
      this.clientError(null);
      this.serverErrors([]);
      GroupsRepository.getGroupDetails(this.businessId, newValue)
        .then((groupDto: GroupDto) => {
          if (this.selectedItemIdObservable() !== groupDto.groupId) {
            return;
          }
          this.hasFocus(true);
          this.groupDetails.groupName(groupDto.groupDetails.groupName);
          if (groupDto.groupDetails.address) {
            this.groupDetails.address(groupDto.groupDetails.address);
            this.groupDetails.isLocationAssigned(true);
          } else {
            this.groupDetails.address(null);
            this.groupDetails.isLocationAssigned(false);
          }
          const initialCoord = groupDto.groupDetails.coordinate ?
            groupDto.groupDetails.coordinate : this.defaultCoordinate;
          this.groupDetails.coordinate(initialCoord);
          this.initialCoordinate(initialCoord);
          this.setAvailableUsers(groupDto.availablePersonas);
          this.setAssignedUsers(groupDto.assignedGroupMembers);
          this.isDataAvailable(true);
          this.usersTabProps.selectedItemId = newValue;
        })
        .catch((jqXhr: any) => {
          this.formSubmitted(false);

          if (jqXhr.serverErrorMessages) {
            this.serverErrors(jqXhr.serverErrorMessages);
            return;
          }

          if (!jqXhr.errorHasBeenLogged) {
            logger.error('UnexpectedErrorWhileGettingGroup', null, jqXhr);
          }
        });
    }
  };

  private setAddPageModeObservables = (): void => {
    this.titleResourceKey('AddGroupTitle');
    this.inAddMode(true);
  };

  private setAvailableUsers = (availablePersonas: PersonaDisplayNameSummaryDto[]): void => {
    const availableUserArray: DualListBoxItem[] = availablePersonas
      .map((item: any) => new DualListBoxItem(
        item.personaId,
        item.personaDisplayName,
        false,
        item.isPersonaPreregistered,
        this.getPersonaIconDetails(item)));
    this.availableUsers(availableUserArray);
  };

  private setAssignedUsers = (assignedGroupMembers: GroupMemberDto[]): void => {
    const assignedGroupMembersArray: DualListBoxItem[] = assignedGroupMembers
      .map((item: any) => new DualListBoxItem(
        item.memberPersona.personaId,
        item.memberPersona.personaDisplayName,
        item.isGroupPrimaryContact,
        item.memberPersona.isPersonaPreregistered,
        this.getPersonaIconDetails(item.memberPersona)));
    this.assignedUsers(assignedGroupMembersArray);
  };

  private getPersonaIconDetails(persona: any): DualListBoxIconDetails | null {
    if (persona.isPersonaPreregistered) {
      return new DualListBoxIconDetails(constants.preRegisteredUserIconClass, 'UnregisteredUserTitle');
    }
    return null;
  }

  private setEditPageModeObservables = (): void => {
    this.titleResourceKey('');
    this.inEditMode(true);
    this.selectedItemIdObservable.subscribe(this.selectItemIdChangeHandler);
  };

  private getStatusValidationInfo = (): any => {
    if (this.assignedUsers().length > 0) {
      const groupHasPrimaryContacts = this.assignedUsers().some(item => item.isChecked());
      if (!groupHasPrimaryContacts) {
        return {
          resourceKey: { key: 'GroupNoPrimaryContact' },
          tabName: 'group-users-tab'
        };
      }
    }
    if (this.groupDetails.isLocationAssigned() && !this.groupDetails.address()?.trim()) {
      return {
        resourceKey: { key: 'GroupNoAddress' },
        tabName: 'group-location-tab'
      };
    }
    return null;
  };

  private resetLocation(): void {
    this.groupDetails.address(null);
    this.groupDetails.coordinate(null);
  }
}

// The default export returns the component details object to register with KO
export default { viewModel: GroupsDetailsViewModel, template: template };
